import type { BillingCountryCurrency, BillingPlan, BillingPlanPrice } from './billingData';
import { formatApiDateTime } from '../admin/dateTimeUtils';

export function formatUpdatedAt(updatedAt: string): string {
    return updatedAt ? formatApiDateTime(updatedAt) : 'Not saved yet';
}

export function formatCurrency(amount: number, currency: string): string {
    try {
        return new Intl.NumberFormat('en-US', {
            style: 'currency',
            currency,
            minimumFractionDigits: 0,
            maximumFractionDigits: 0,
        }).format(amount);
    } catch {
        return `${currency} ${Math.round(amount)}`;
    }
}

export function parseNumber(value: string, fallback = 0): number {
    const parsed = Number.parseFloat(value);
    return Number.isFinite(parsed) ? parsed : fallback;
}

export function getCountryCurrencyByCode(
    countryCurrencies: BillingCountryCurrency[],
    countryCode: string,
): BillingCountryCurrency | undefined {
    return countryCurrencies.find((countryCurrency) => countryCurrency.countryCode === countryCode);
}

export function getCurrencyCodeForCountry(
    countryCurrencies: BillingCountryCurrency[],
    countryCode: string,
): string {
    return getCountryCurrencyByCode(countryCurrencies, countryCode)?.currency ?? 'USD';
}

export function getPlanPrices(plan: BillingPlan): BillingPlanPrice[] {
    return Array.isArray(plan.prices) ? plan.prices : [];
}

export function getPlanPricesForCountryCurrencies(
    plan: BillingPlan,
    countryCurrencies: BillingCountryCurrency[],
): BillingPlanPrice[] {
    const enabledCountryCodes = new Set(countryCurrencies.map((countryCurrency) => countryCurrency.countryCode));
    return getPlanPrices(plan).filter((price) => enabledCountryCodes.has(price.countryCode));
}

export function getActivePlanPrices(
    plan: BillingPlan,
    countryCurrencies?: BillingCountryCurrency[],
): BillingPlanPrice[] {
    const prices = countryCurrencies ? getPlanPricesForCountryCurrencies(plan, countryCurrencies) : getPlanPrices(plan);
    return prices.filter((price) => price.isActive);
}

export function getPlanPriceCount(plan: BillingPlan, countryCurrencies?: BillingCountryCurrency[]): number {
    return countryCurrencies ? getPlanPricesForCountryCurrencies(plan, countryCurrencies).length : getPlanPrices(plan).length;
}

export function getStartingPlanPrice(plan: BillingPlan, countryCurrencies: BillingCountryCurrency[]): string {
    const prices = getActivePlanPrices(plan, countryCurrencies);
    if (prices.length === 0) return 'No active market prices';

    const lowestPrice = prices.reduce((currentLowest, candidate) =>
        candidate.priceAmount < currentLowest.priceAmount ? candidate : currentLowest,
    );

    return `${formatCurrency(lowestPrice.priceAmount, getCurrencyCodeForCountry(countryCurrencies, lowestPrice.countryCode))} in ${lowestPrice.countryCode}`;
}

export function getPlanPriceSummary(plan: BillingPlan, countryCurrencies: BillingCountryCurrency[]): string {
    const prices = getPlanPricesForCountryCurrencies(plan, countryCurrencies);
    if (prices.length === 0) return 'No market pricing configured';

    return prices
        .slice(0, 3)
        .map(
            (price) =>
                `${price.countryCode}: ${formatCurrency(price.priceAmount, getCurrencyCodeForCountry(countryCurrencies, price.countryCode))}`,
        )
        .join(' | ');
}
